import React, { useState, useEffect, useCallback } from "react";
import "./agentmanagement.css";
import "./portal-components.css";
import { useAuth } from "../../context/AuthContext";
import { API_BASE_URL } from "../../config/apiConfig";

const AgentManagement = () => {
  const { user } = useAuth();

  // State
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registrationKey, setRegistrationKey] = useState("");
  const [keyLoading, setKeyLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Fetch registered agents
  const fetchAgents = useCallback(async () => {
    if (!user?.username) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/agents/registered?username=${encodeURIComponent(user.username)}`
      );
      const data = await res.json();
      if (data.status === "success") {
        setAgents(data.data?.agents || []);
      } else {
        setError(data.reason || "Failed to fetch agents");
      }
    } catch (err) {
      console.error("Failed to fetch agents:", err);
      setError("Network error fetching agents");
    } finally {
      setLoading(false);
    }
  }, [user?.username]);

  // Fetch registration key
  const fetchRegistrationKey = useCallback(async () => {
    if (!user?.username) return;
    setKeyLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/agents/key?username=${encodeURIComponent(user.username)}`
      );
      const data = await res.json();
      if (data.status === "success") {
        setRegistrationKey(data.data?.registration_key || "");
      }
    } catch (err) {
      console.error("Failed to fetch registration key:", err);
    } finally {
      setKeyLoading(false);
    }
  }, [user?.username]);

  // Regenerate registration key
  const regenerateKey = async () => {
    if (!user?.username) return;
    setKeyLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/agents/key/regenerate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: user.username }),
      });
      const data = await res.json();
      if (data.status === "success") {
        setRegistrationKey(data.data?.registration_key || "");
      } else {
        setError(data.reason || "Failed to regenerate key");
      }
    } catch (err) {
      console.error("Failed to regenerate key:", err);
      setError("Network error regenerating key");
    } finally {
      setKeyLoading(false);
    }
  };

  // Delete agent
  const deleteAgent = async (agentId) => {
    if (!user?.username) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}/agents/${agentId}?username=${encodeURIComponent(user.username)}`,
        { method: "DELETE" }
      );
      const data = await res.json();
      if (data.status === "success") {
        setAgents((prev) => prev.filter((a) => a.id !== agentId));
        setDeleteConfirm(null);
      } else {
        setError(data.reason || "Failed to delete agent");
      }
    } catch (err) {
      console.error("Failed to delete agent:", err);
      setError("Network error deleting agent");
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Get status color
  const getStatusClass = (status) => {
    switch (status) {
      case "online": return "online";
      case "warning": return "warning";
      case "offline":
      default: return "offline";
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  useEffect(() => {
    fetchAgents();
    fetchRegistrationKey();
    const interval = setInterval(fetchAgents, 30000);
    return () => clearInterval(interval);
  }, [fetchAgents, fetchRegistrationKey]);

  return (
    <>
      <div className="right-top">
        <div className="right-top-title">Agent Management</div>
      </div>

      <div className="right-middle">
        {/* Error Alert */}
        {error && (
          <div className="alert alert-error">
            <div className="alert-content">{error}</div>
            <button className="alert-close" onClick={() => setError(null)}>×</button>
          </div>
        )}

        {/* Registration Key Card */}
        <div className="card-section">
          <div className="card-section-header">
            <div>
              <h3 className="card-section-title">Registration Key</h3>
              <p className="card-section-subtitle">Use this key to register new agents with your account</p>
            </div>
          </div>
          <div className="key-row">
            {keyLoading ? (
              <div className="loading-state">Loading...</div>
            ) : (
              <>
                <div className="key-display">
                  <code>{registrationKey}</code>
                  <button
                    className="btn-icon"
                    onClick={() => copyToClipboard(registrationKey)}
                    title="Copy to clipboard"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                  </button>
                </div>
                <button className="btn-secondary" onClick={regenerateKey}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M23 4v6h-6"/>
                    <path d="M1 20v-6h6"/>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                  </svg>
                  <span>Regenerate Key</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Registered Agents Card */}
        <div className="card-section">
          <div className="card-section-header">
            <div>
              <h3 className="card-section-title">Registered Agents ({agents.length})</h3>
            </div>
            <button className="btn-secondary" onClick={fetchAgents}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M23 4v6h-6"/>
                <path d="M1 20v-6h6"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
              <span>Refresh</span>
            </button>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              Loading agents...
            </div>
          ) : agents.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                  <line x1="8" y1="21" x2="16" y2="21"/>
                  <line x1="12" y1="17" x2="12" y2="21"/>
                </svg>
              </div>
              <h4 className="empty-state-title">No Agents Registered</h4>
              <p className="empty-state-text">Use the registration key above to connect your first agent</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Agent Name</th>
                    <th>Hostname</th>
                    <th>IP Address</th>
                    <th>Platform</th>
                    <th>Last Seen</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((agent) => (
                    <tr key={agent.id}>
                      <td>
                        <span className={`status-dot ${getStatusClass(agent.connection_status)}`} />
                      </td>
                      <td>
                        <div className="cell-name">{agent.agent_name}</div>
                        <div className="cell-subtitle">v{agent.agent_version || "1.0"}</div>
                      </td>
                      <td>{agent.hostname || "-"}</td>
                      <td>{agent.ip_address || "-"}</td>
                      <td>
                        <span className="platform-badge">{agent.platform || "Unknown"}</span>
                      </td>
                      <td className="cell-muted">{formatDate(agent.last_seen_at)}</td>
                      <td className="cell-actions">
                        {deleteConfirm === agent.id ? (
                          <div className="btn-group">
                            <button className="btn-danger btn-sm" onClick={() => deleteAgent(agent.id)}>
                              Yes, Delete
                            </button>
                            <button className="btn-secondary btn-sm" onClick={() => setDeleteConfirm(null)}>
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button className="btn-danger btn-sm" onClick={() => setDeleteConfirm(agent.id)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            </svg>
                            <span>Delete</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="right-bottom-bar" />
    </>
  );
};

export default AgentManagement;
